import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { Button, Card, CardHeader, Grid, Stack, TextField } from '@mui/material';
import { AppBadge, AppCardSection, AppDescriptionList, AppPage, AppToast } from '@shared';

import axiosRails from '@utils/axios-rails';
import { ta } from '@utils/index';
import { valueToBadgeStatus } from '@utils/columns';
import EvidenceList from 'components/shared/EvidenceList';
import AppModal from 'components/shared/AppModal';

const propTypes = {
  record: PropTypes.object.isRequired,
  evidences: PropTypes.array,
  updateURL: PropTypes.string,
  breadcrumbs: PropTypes.array.isRequired,
}

const defaultProps = {
  evidences: [],
}

const ResourceTransferShow = (props) => {
  const [record, setRecord] = useState(props.record);
  const [toast, setToast] = useState(null);
  const [rejectNotes, setRejectNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const rejectModalRef = React.useRef(null);

  // Same endpoint the index uses; resolving from here keeps the decision next to the evidence.
  const resolveTransfer = async (status, notes = null) => {
    setLoading(true);
    try {
      const itemURL = props.updateURL.replace(":id", record.id);
      const payload = { resource_transfer: notes === null ? { status } : { status, notes } };
      const response = await axiosRails.put(itemURL, payload);

      if (response.data.success) {
        setRecord({ ...record, ...response.data.record });
        setToast(status === "accepted" ? "Traslado aceptado" : "Traslado rechazado");
        rejectModalRef.current?.close();
      } else {
        setToast("Error al actualizar el traslado");
      }
    } catch (error) {
      setToast(I18n.t("messages.request_error"));
    }
    setLoading(false);
  };

  const handleAccept = () => {
    if (confirm("¿Está seguro de aceptar el traslado?")) resolveTransfer("accepted");
  };

  return (
      <AppPage
        title={ I18n.t("activerecord.models.resource_transfer", { count: 1 }) }
        subtitle={(
          <AppBadge status={ valueToBadgeStatus[record.status] } >
            {I18n.t(`enum.status.${record.status}`)}
          </AppBadge>
        )}
        breadcrumbs={ props.breadcrumbs }
      >
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <Card>
              <CardHeader title="Información General" titleTypographyProps={{ variant: 'h6' }} />
              <AppCardSection>
              <AppDescriptionList
                items={[
                  { term: ta("general.type"), description: I18n.t(`enum.polymorphic_type.${record.resource_type}`) },
                  { term: ta("resource_transfer.transfer_type"), description: I18n.t(`enum.transfer_type.${record.transfer_type}`) },
                  { term: ta("resource_transfer.created_at"), description: I18n.l("time.formats.date_only", record.created_at) },
                  { term: ta("resource_transfer.requested_by"), description: record.requested_by.name },
                  { term: ta("resource_transfer.resolved_by"), description: record.resolved_by && record.resolved_by.name },
                  { term: ta("general.notes"), description: record.notes },
                ]}
              />
              </AppCardSection>
            </Card>

            <EvidenceList evidences={ props.evidences } title="Evidencias del mensajero" />

            { record.status === "pending" && !!props.updateURL &&
              <Card>
                <CardHeader title="Resolver el traslado" titleTypographyProps={{ variant: 'h6' }} />
                <AppCardSection>
                  <p className="mb12">Al aceptar, el producto queda registrado como recibido.</p>
                  <Stack direction="row" spacing={1}>
                    <Button variant="contained" disabled={ loading } onClick={ handleAccept }>Aceptar</Button>
                    <Button
                      variant="outlined"
                      color="error"
                      disabled={ loading }
                      onClick={ () => { setRejectNotes(""); rejectModalRef.current?.open(); } }
                    >
                      Rechazar
                    </Button>
                  </Stack>
                </AppCardSection>
              </Card>
            }
          </Grid>

          <Grid item xs={12} md={4}>
            <Card>
              <CardHeader title="Información Orden" titleTypographyProps={{ variant: 'h6' }} />
              <AppCardSection>
              <AppDescriptionList
                items={[
                  { term: ta("general.id"), description: record.resource.provider_id },
                  { term: ta("dispatch_order.order_number"), description: record.resource.order_number },
                  { term: I18n.t("activerecord.models.team", { count: 1 }), description: record.team_name },
                ]}
              />
              </AppCardSection>
            </Card>
          </Grid>
        </Grid>

        <AppModal
          ref={ rejectModalRef }
          title="Rechazar traslado"
          primaryAction={{
            content: "Rechazar",
            onAction: () => resolveTransfer("rejected", rejectNotes),
            loading: loading,
            disabled: !rejectNotes,
            destructive: true,
          }}
          secondaryActions={[{ content: "Cancelar", onAction: () => rejectModalRef.current?.close() }]}
        >
          <TextField
            fullWidth
            InputLabelProps={{ shrink: true }}
            label="Motivo del rechazo"
            value={ rejectNotes }
            onChange={ (event) => setRejectNotes(event.target.value) }
            multiline
            minRows={3}
          />
        </AppModal>

        { !!toast && <AppToast content={ toast } onDismiss={ () => setToast(null) } /> }
      </AppPage>
  )
};

ResourceTransferShow.propTypes = propTypes;
ResourceTransferShow.defaultProps = defaultProps;

export default ResourceTransferShow;